import React, { useState, useEffect, useMemo } from 'react';
import {
    Row,
    Col,
    Card,
    Typography,
    Select,
    Button,
    Space,
    Divider,
    Tag,
    Spin,
    Transfer,
} from 'antd';
import {
    SaveOutlined,
} from '@ant-design/icons';
import toast from 'react-hot-toast';
import { ROLE_OPTIONS, PERMISSION_OPTIONS } from '../../shared/utils/enum';
import { internalApi } from '../../common/axios';
import '@ant-design/v5-patch-for-react-19';

const { Title, Text } = Typography;

const RolePermission = () => {

    const [selectedRole, setSelectedRole] = useState(ROLE_OPTIONS[0]?.value ?? null);
    const [allRolePermissions, setAllRolePermissions] = useState({});
    const [loading, setLoading] = useState(false);

    // Transfer용 데이터 소스
    const permissionData = useMemo(
        () =>
            PERMISSION_OPTIONS.map((p) => ({
                key: String(p.value),
                title: p.label,
                value: p.value,
            })),
        []
    );

    // 현재 선택된 역할의 targetKeys (할당된 권한)
    const targetKeys = useMemo(() => {
        const assigned = allRolePermissions[selectedRole] || [];
        return assigned.map((v) => String(v));
    }, [allRolePermissions, selectedRole]);

    useEffect(() => {
        fetchRolePermissions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const uniqueArray = (arr) =>
        Array.from(new Set((arr || []).filter((v) => v !== null && v !== undefined)));

    const fetchRolePermissions = async () => {
        try {
            setLoading(true);
            const response = await internalApi.get('admin/role/permission');

            const raw = response.data.rolePermissions || {};
            // 각 역할별 권한 배열을 유니크화
            const normalized = Object.fromEntries(
                Object.entries(raw).map(([roleId, perms]) => [roleId, uniqueArray(perms)])
            );

            setAllRolePermissions(normalized);
        } catch (error) {
            toast.error('역할 권한을 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    };


    const handleRoleChange = (value) => {
        setSelectedRole(value);
    };

    const handleTransferChange = (nextTargetKeys) => {
        // Transfer는 보통 중복을 만들지 않지만 안전하게 유니크 처리
        const nextValues = uniqueArray(
            nextTargetKeys
                .map((k) => permissionData.find((d) => d.key === k)?.value)
                .filter((v) => v !== undefined)
        );

        setAllRolePermissions((prev) => ({
            ...prev,
            [selectedRole]: nextValues,
        }));
    };

    const saveRolePermissions = async () => {
        try {
            setLoading(true);
            // 저장 직전에도 유니크 보장
            const uniquePermissions = uniqueArray(allRolePermissions[selectedRole]);

            await internalApi.post('admin/role/permission', {
                roleId: selectedRole,
                permissions: uniquePermissions,
            });

            // 서버 최신 데이터로 동기화
            await fetchRolePermissions();
            toast.success('역할 권한이 성공적으로 저장되었습니다.');
        } catch (error) {
            toast.error('권한 저장에 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const saveAllRolePermissions = async () => {
        try {
            setLoading(true);
            // 전체 일괄 저장 API 엔드포인트에 맞게 수정하세요.
            // await internalApi.put('/role-permissions', allRolePermissions);
            toast.success('모든 역할 권한이 성공적으로 저장되었습니다.');
        } catch (error) {
            toast.error('권한 저장에 실패했습니다.');
            // console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const selectedRoleLabel =
        ROLE_OPTIONS.find((role) => role.value === selectedRole)?.label || '';

    return (
        <div className="role-permission-container" style={{ padding: 24 }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                        <Title level={3} style={{ margin: 0 }}>
                            역할 권한 관리
                        </Title>
                        <Text type="secondary">역할별 접근 권한을 직관적으로 구성하고 저장하세요.</Text>
                    </Col>
                    <Col>
                        <Space>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={saveRolePermissions}
                                disabled={loading || selectedRole === null}
                            >
                                현재 역할 저장
                            </Button>
                            {/*<Button*/}
                            {/*    icon={<DatabaseOutlined />}*/}
                            {/*    onClick={saveAllRolePermissions}*/}
                            {/*    disabled={loading}*/}
                            {/*>*/}
                            {/*    전체 저장*/}
                            {/*</Button>*/}
                        </Space>
                    </Col>
                </Row>

                <Card style={{ marginBottom: 16, paddingBottom: 8 }}>
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} md={12}>
                            <Space direction="vertical" style={{ width: '100%' }} size={4}>
                                <Text strong>역할 선택</Text>
                                <Select
                                    value={selectedRole}
                                    onChange={handleRoleChange}
                                    style={{ width: '100%' }}
                                    placeholder="역할 선택"
                                    options={ROLE_OPTIONS.map((r) => ({
                                        label: r.label,
                                        value: r.value,
                                    }))}
                                />
                            </Space>
                        </Col>
                        <Col xs={24} md={12}>
                            <Text type="secondary">
                                선택한 역할에 권한을 이동하여 추가/제거할 수 있습니다.
                            </Text>
                        </Col>
                    </Row>
                </Card>

                <Spin spinning={loading}>
                    {selectedRole !== null && (
                        <Row gutter={[16, 16]}>
                            <Col xs={24} lg={24}>
                                <Card>
                                    <Row justify="space-between" align="middle">
                                        <Col>
                                            <Title level={5} style={{ marginBottom: 0 }}>
                                                {selectedRoleLabel} 권한 구성
                                            </Title>
                                            <Text type="secondary">사용 가능한 권한과 할당된 권한을 이동하세요.</Text>
                                        </Col>
                                    </Row>
                                    <Divider />
                                    <Transfer
                                        dataSource={permissionData}
                                        titles={['사용 가능', '할당됨']}
                                        listStyle={{ width: '100%', height: 360 }}
                                        targetKeys={targetKeys}
                                        onChange={handleTransferChange}
                                        showSearch
                                        render={(item) => item.title}
                                        oneWay={false}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    )}

                    <Card style={{ marginTop: 16 }}>
                        <Title level={5} style={{ marginBottom: 12 }}>
                            전체 역할 권한 현황
                        </Title>
                        <Row gutter={[12, 12]}>
                            {ROLE_OPTIONS.map((role) => {
                                const assignedValues = uniqueArray(allRolePermissions[role.value]); // 렌더 시에도 한 번 더 유니크
                                return (
                                    <Col xs={24} key={String(role.value)}>
                                        <Card size="small" bordered>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Text strong style={{ color: '#1677ff' }}>{role.label}</Text>
                                                <div>
                                                    {assignedValues.length === 0 ? (
                                                        <Text type="secondary">할당된 권한이 없습니다.</Text>
                                                    ) : (
                                                        assignedValues.map((permissionValue) => {
                                                            const perm = PERMISSION_OPTIONS.find((p) => p.value === permissionValue);
                                                            // 안정적인 key: 역할ID-권한ID 조합
                                                            return perm ? (
                                                                <Tag
                                                                    key={`${String(role.value)}-${String(permissionValue)}`}
                                                                    color="geekblue"
                                                                    style={{ marginBottom: 6 }}
                                                                >
                                                                    {perm.label}
                                                                </Tag>
                                                            ) : null;
                                                        })
                                                    )}
                                                </div>
                                            </Space>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    </Card>
                </Spin>
            </div>
    );
};

export default RolePermission;